import { useNavigate } from "react-router-dom";
import { Sparkles, Clapperboard, Play, Clock } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { Pill, DemoBadge, cx } from "@/components/shared";

const STATUS = {
  ready: { tone: "green", label: "Final Cut" },
  generating: { tone: "gold", label: "In Production" },
  queued: { tone: "purple", label: "On Deck" },
  failed: { tone: "red", label: "Needs Attention" },
};

export default function FilmCard({ project, demo = false, index = 0 }) {
  const navigate = useNavigate();
  const p = project;
  const s = STATUS[p.status] || { tone: "slate", label: p.status || "Draft" };
  const inProd = p.status === "generating" || p.status === "queued";

  return (
    <button
      type="button"
      onClick={() => !demo && navigate(`/projects/${p.id}`)}
      data-testid={demo ? "demo-film-card" : "project-card"}
      className={cx(
        "film-card cine-panel overflow-hidden text-left w-full group",
        demo ? "cursor-default" : "cursor-pointer"
      )}
      style={{ animation: `stage-in .5s cubic-bezier(.2,.7,.2,1) both`, animationDelay: `${index * 0.05}s` }}
    >
      <div className="aspect-[16/10] relative bg-gradient-to-br from-[#141428] to-[#0A0A16] overflow-hidden">
        {p.cover ? (
          <img
            src={demo ? p.cover : mediaUrl(p.cover)}
            alt={p.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-700">
            <Clapperboard className="w-9 h-9" />
          </div>
        )}
        {/* poster scrim + film sprockets */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-3 film-strip opacity-40" />

        <div className="absolute top-2.5 left-2.5 flex items-center gap-2">
          {demo && <DemoBadge />}
        </div>
        <div className="absolute top-2.5 right-2.5">
          <Pill tone={s.tone}>{s.label}</Pill>
        </div>

        {/* play affordance on hover */}
        {!demo && (
          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur border border-white/30 grid place-items-center">
              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
            </span>
          </div>
        )}

        {inProd && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
            <div className="h-full rail-line" style={{ width: `${p.progress || 0}%` }} />
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="font-display font-semibold text-white truncate">{p.title}</div>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500 font-mono">
          {p.config?.style && <span className="truncate">{p.config.style}</span>}
          {p.config?.duration && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {p.config.duration}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
